// web/src/routes/dashboard/TrainerDashboard.jsx
// Today, for a trainer. Answers: "What are my horses doing?"
//
// The planner page — see components/dashboard/Planner. A trainer's day is
// appointments down the right, so this is the dashboard the idiom fits best:
// the lessons ARE the appointment schedule.
//
// Every row goes to its horse. Rides and flags already did; lessons and notes
// pointed nowhere, because the payload sent horse_name without horse_id.
//
// The page flips: the little month turns it to another day, and the lessons and
// rides follow. The flags and the recent notes don't — they are standing facts
// about horses in this trainer's string, not facts about a date.

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { colorClass } from '../../lib/lessonTypes';
import { clock } from '../../lib/shifts';
import MiniMonth from '../../components/dashboard/MiniMonth';
import {
  DayHeader, Panel, Empty, Row, Rows, PlannerGrid, PlannerSkeleton,
} from '../../components/dashboard/Planner';

export default function TrainerDashboard() {
  // The day being read lives in the URL, so a particular day is a link and the
  // back button walks back through the days you turned to.
  const [params, setParams] = useSearchParams();
  const date = params.get('date');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'trainer', date ?? 'today'],
    queryFn: () => api.get(date ? `/dashboard/trainer?date=${date}` : '/dashboard/trainer'),
    // The day you were on stays up while the next one loads, rather than
    // blanking the page on every tap of the calendar.
    placeholderData: (prev) => prev,
  });

  const today = data?.today ?? null;
  const shown = data?.date ?? date ?? today;

  const goTo = (d) => {
    const next = new URLSearchParams(params);
    if (!d || d === today) next.delete('date');
    else next.set('date', d);
    setParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <DayHeader
        today={today}
        date={shown}
        onToday={() => goTo(null)}
        action={
          <Link to="/schedule/availability" className="text-sm text-hunter hover:underline pb-1">
            My availability →
          </Link>
        }
      />

      {isLoading ? (
        <PlannerSkeleton />
      ) : (
        <PlannerGrid
          aside={
            <>
              {/* Tapping a date turns this page to it rather than leaving for
                  the schedule — the day you flip to is still a planner page. */}
              <MiniMonth today={today} selected={shown} onPick={goTo} />
              <Flags flags={data?.flags} />
              <RecentNotes notes={data?.recentNotes} />
            </>
          }
        >
          <Lessons lessons={data?.lessons} today={shown} />
          <Rides rides={data?.rides} />
        </PlannerGrid>
      )}
    </div>
  );
}

function Lessons({ lessons = [], today }) {
  return (
    <Panel
      title="Appointment schedule"
      action={
        <Link to={`/schedule?view=day&date=${today ?? ''}`} className="normal-case tracking-normal hover:underline">
          the whole day →
        </Link>
      }
    >
      {lessons.length === 0 ? (
        <Empty>No lessons booked.</Empty>
      ) : (
        <Rows>
          {lessons.map((l) => (
            <Row key={l.id} to={l.horseId ? `/horses/${l.horseId}` : '/schedule'} className="flex items-baseline gap-3">
              <span className="w-16 shrink-0 tabular-nums text-hunter font-medium">
                {l.startTime ? clock(l.startTime).replace(':00', '') : 'TBD'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {l.lessonTypeName && (
                    <span className={'inline-block w-2 h-2 rounded-full mr-1.5 ' + colorClass(l.lessonTypeColor, 'dot')} />
                  )}
                  <span className="font-medium">{l.riderName}</span>
                  {l.horseName && <span className="text-ink-500"> · {l.horseName}</span>}
                </span>
                {l.lessonTypeName && <span className="block text-xs text-ink-400">{l.lessonTypeName}</span>}
              </span>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function Rides({ rides = [] }) {
  return (
    <Panel title="Rides to get done" count={rides.filter((r) => r.status === 'pending').length}>
      {rides.length === 0 ? (
        <Empty>No rides on the board.</Empty>
      ) : (
        <Rows>
          {rides.map((r) => (
            <Row key={r.taskId} to={r.horseId ? `/horses/${r.horseId}` : '/board'} className="flex items-baseline gap-3">
              <span className="w-16 shrink-0 tabular-nums text-hunter font-medium">
                {r.time ? clock(r.time).replace(':00', '') : labelForBlock(r.timeBlock)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  <span className="font-medium">{r.horseName}</span>
                  <span className="text-ink-500"> · {r.rideType ?? 'ride'}</span>
                </span>
                {r.stall && <span className="block text-xs text-ink-400">Stall {r.stall}</span>}
              </span>
              <StatusPill status={r.status} />
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function Flags({ flags = [] }) {
  return (
    <Panel title="Needs a look" count={flags.length}>
      {flags.length === 0 ? (
        <Empty>Nothing flagged.</Empty>
      ) : (
        <Rows>
          {flags.map((f) => (
            <Row key={f.horseId} to={`/horses/${f.horseId}`}>
              <span className="block font-medium">{f.horseName}</span>
              <span className="block text-ink-600 line-clamp-2">{f.reason}</span>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function RecentNotes({ notes = [] }) {
  return (
    <Panel title="Recent notes" count={notes.length}>
      {notes.length === 0 ? (
        <Empty>No notes yet.</Empty>
      ) : (
        <Rows>
          {notes.map((n) => (
            <Row key={n.id} to={n.horseId ? `/horses/${n.horseId}` : '/horses'}>
              <span className="flex items-baseline justify-between gap-2">
                <span className="font-medium truncate">{n.horseName}</span>
                <span className="shrink-0 text-xs text-ink-400">{n.authorName}</span>
              </span>
              <span className="block text-ink-600 line-clamp-2">{n.body}</span>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function StatusPill({ status }) {
  const styles = {
    done: 'bg-hunter/15 text-hunter',
    pending: 'bg-ink-100 text-ink-600',
    skipped: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-ink-100 text-ink-400',
  };
  return (
    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full capitalize ${styles[status] ?? styles.pending}`}>
      {status}
    </span>
  );
}

function labelForBlock(block) {
  return { am: 'AM', noon: 'Noon', pm: 'PM', nc: 'NC' }[block] ?? block;
}
